/**
 * Serialize a SceneObject's mesh data to a human-readable plaintext string
 * for debugging purposes. Includes object metadata, vertices, topology (faces),
 * edges, loop normals, UVs, and render triangle indices.
 */

import {
  buildEdgesAndCornerEdges,
  topologyFaceVerts,
  triangulateTopologyForRendering,
} from "../modeling/topology.ts";
import { asLoopNormals, type SceneObject } from "../modeling/types.ts";

interface MeshDebugSerializeOptions {
  title?: string;
  sourceFaceIndices?: readonly number[];
}

export function buildSelectedFaceDebugMesh(
  obj: SceneObject,
  selectedFaces: ReadonlySet<number>,
): { mesh: SceneObject; sourceFaceIndices: number[] } | null {
  if (selectedFaces.size === 0) return null;

  const sourceFaceIndices = Array.from(selectedFaces).sort((a, b) => a - b);
  const selectedVerts = new Set<number>();
  for (const faceIndex of sourceFaceIndices) {
    for (const vertex of topologyFaceVerts(obj.topology, faceIndex)) {
      selectedVerts.add(vertex);
    }
  }
  if (selectedVerts.size === 0) return null;

  const sortedVerts = Array.from(selectedVerts).sort((a, b) => a - b);
  const remap = new Map<number, number>();
  for (let i = 0; i < sortedVerts.length; i++) remap.set(sortedVerts[i], i);

  const vertices = new Float32Array(sortedVerts.length * 3);
  for (let i = 0; i < sortedVerts.length; i++) {
    const sourceVertex = sortedVerts[i];
    vertices[i * 3] = obj.vertices[sourceVertex * 3];
    vertices[i * 3 + 1] = obj.vertices[sourceVertex * 3 + 1];
    vertices[i * 3 + 2] = obj.vertices[sourceVertex * 3 + 2];
  }

  const corners: number[] = [];
  const offsets = new Uint32Array(sourceFaceIndices.length + 1);
  const loopNormals = new Float32Array(
    sourceFaceIndices.reduce(
      (count, faceIndex) =>
        count +
        (obj.topology.offsets[faceIndex + 1] - obj.topology.offsets[faceIndex]),
      0,
    ) * 3,
  );
  const uvs =
    obj.uvs && obj.uvs.length > 0
      ? new Float32Array((loopNormals.length / 3) * 2)
      : undefined;

  let cornerCursor = 0;
  for (
    let faceCursor = 0;
    faceCursor < sourceFaceIndices.length;
    faceCursor++
  ) {
    const faceIndex = sourceFaceIndices[faceCursor];
    const start = obj.topology.offsets[faceIndex];
    const end = obj.topology.offsets[faceIndex + 1];
    for (let cornerIndex = start; cornerIndex < end; cornerIndex++) {
      corners.push(remap.get(obj.topology.corners[cornerIndex])!);
      loopNormals[cornerCursor * 3] = obj.loopNormals[cornerIndex * 3];
      loopNormals[cornerCursor * 3 + 1] = obj.loopNormals[cornerIndex * 3 + 1];
      loopNormals[cornerCursor * 3 + 2] = obj.loopNormals[cornerIndex * 3 + 2];
      if (uvs) {
        uvs[cornerCursor * 2] = obj.uvs![cornerIndex * 2];
        uvs[cornerCursor * 2 + 1] = obj.uvs![cornerIndex * 2 + 1];
      }
      cornerCursor++;
    }
    offsets[faceCursor + 1] = cornerCursor;
  }

  const cornersArray = new Uint32Array(corners);
  const { edges, cornerEdges, edgeFlags } = buildEdgesAndCornerEdges(
    offsets,
    cornersArray,
  );
  const topology = {
    offsets,
    corners: cornersArray,
    edges,
    cornerEdges,
    edgeFlags,
  };

  return {
    mesh: {
      ...obj,
      vertices,
      indices: triangulateTopologyForRendering(topology),
      loopNormals: asLoopNormals(loopNormals),
      topology,
      uvs,
    },
    sourceFaceIndices,
  };
}

export function serializeMeshDebug(
  obj: SceneObject,
  options: MeshDebugSerializeOptions = {},
): string {
  const lines: string[] = [];
  const { vertices, loopNormals: normals, topology, uvs, indices } = obj;
  const vertCount = vertices.length / 3;
  const faceCount = topology.offsets.length - 1;
  const cornerCount = topology.corners.length;
  const edgeCount = topology.edges.length / 2;

  // -- Header --
  lines.push(`=== ${options.title ?? "Mesh Debug Export"} ===`);
  lines.push(`name: ${obj.name}`);
  lines.push(`id: ${obj.id}`);
  lines.push(`kind: ${obj.objectKind}`);
  lines.push(`shading: ${obj.shading}`);
  lines.push("vertexSpace: local_object");
  lines.push(`objectPosition: [${obj.position.join(", ")}]`);
  lines.push(
    `objectRotation: [${obj.rotation.map((r) => r.toFixed(6)).join(", ")}]`,
  );
  lines.push(`objectScale: [${obj.scale.join(", ")}]`);
  lines.push(`vertexCount: ${vertCount}`);
  lines.push(`faceCount: ${faceCount}`);
  lines.push(`cornerCount: ${cornerCount}`);
  lines.push(`edgeCount: ${edgeCount}`);
  lines.push(`triangleIndexCount: ${indices.length}`);
  if (options.sourceFaceIndices && options.sourceFaceIndices.length > 0) {
    lines.push(`sourceFaceIndices: ${options.sourceFaceIndices.join(" ")}`);
  }
  lines.push("");

  // -- Vertices --
  lines.push(`--- Vertices (${vertCount}) ---`);
  for (let i = 0; i < vertCount; i++) {
    const x = vertices[i * 3];
    const y = vertices[i * 3 + 1];
    const z = vertices[i * 3 + 2];
    lines.push(`v${i}: ${x.toFixed(6)} ${y.toFixed(6)} ${z.toFixed(6)}`);
  }
  lines.push("");

  // -- Loop normals --
  lines.push(`--- Loop Normals (${cornerCount}) ---`);
  for (let f = 0; f < faceCount; f++) {
    const start = topology.offsets[f];
    const end = topology.offsets[f + 1];
    for (let c = start; c < end; c++) {
      const localCorner = c - start;
      const vertex = topology.corners[c];
      const x = normals[c * 3];
      const y = normals[c * 3 + 1];
      const z = normals[c * 3 + 2];
      lines.push(
        `ln${c} [f${f} c${localCorner} v${vertex}]: ${x.toFixed(6)} ${y.toFixed(6)} ${z.toFixed(6)}`,
      );
    }
  }
  lines.push("");

  // -- UVs --
  if (uvs && uvs.length > 0) {
    lines.push(`--- UVs (${cornerCount}) ---`);
    for (let f = 0; f < faceCount; f++) {
      const start = topology.offsets[f];
      const end = topology.offsets[f + 1];
      for (let c = start; c < end; c++) {
        const localCorner = c - start;
        const vertex = topology.corners[c];
        lines.push(
          `uv${c} [f${f} c${localCorner} v${vertex}]: ${uvs[c * 2].toFixed(6)} ${uvs[c * 2 + 1].toFixed(6)}`,
        );
      }
    }
    lines.push("");
  }

  // -- Topology (Faces) --
  lines.push(`--- Faces (${faceCount}) ---`);
  for (let f = 0; f < faceCount; f++) {
    const start = topology.offsets[f];
    const end = topology.offsets[f + 1];
    const verts: number[] = [];
    for (let c = start; c < end; c++) {
      verts.push(topology.corners[c]);
    }
    const sides = verts.length;
    const kind = sides === 3 ? "tri" : sides === 4 ? "quad" : `ngon(${sides})`;
    lines.push(`f${f} [${kind}]: ${verts.join(" ")}`);
  }
  lines.push("");

  // -- Edges --
  lines.push(`--- Edges (${edgeCount}) ---`);
  for (let i = 0; i < edgeCount; i++) {
    lines.push(`e${i}: ${topology.edges[i * 2]} ${topology.edges[i * 2 + 1]}`);
  }
  lines.push("");

  // -- Triangle indices --
  lines.push(`--- Triangle Indices (${indices.length / 3} tris) ---`);
  for (let i = 0; i < indices.length; i += 3) {
    lines.push(`t${i / 3}: ${indices[i]} ${indices[i + 1]} ${indices[i + 2]}`);
  }

  return lines.join("\n");
}
